import { useRef, useEffect } from 'react';

/**
 * FishSchool — 多层鱼群动态
 * 
 * 使用真实鱼的 SVG/PNG 素材，分 3~5 个深度层。
 * 不同层：大小/透明度/速度/方向都不同。
 * 80% 的鱼很淡，只有少数鱼比较明显。
 * 
 * 鼠标交互：鱼群非常缓慢地避让。
 */

// 鱼群配置 — 不同深度层
const SCHOOL_LAYERS = [
  { count: 5, sizeRange: [8, 12], speed: 18, opacity: [0.15, 0.3], yRange: [0.15, 0.35], direction: -1, depth: 1 },
  { count: 7, sizeRange: [10, 16], speed: 25, opacity: [0.2, 0.45], yRange: [0.25, 0.55], direction: 1, depth: 2 },
  { count: 4, sizeRange: [14, 22], speed: 35, opacity: [0.3, 0.6], yRange: [0.4, 0.7], direction: -1, depth: 3 },
  { count: 3, sizeRange: [18, 28], speed: 45, opacity: [0.4, 0.75], yRange: [0.5, 0.8], direction: 1, depth: 4 },
];

export default function FishSchool() {
  const containerRef = useRef(null);
  const fishDataRef = useRef([]);
  const animRef = useRef(null);
  const mouseRef = useRef({ x: -999, y: -999 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const W = rect.width;
    const H = rect.height;

    // 生成鱼群数据
    const fishes = [];
    SCHOOL_LAYERS.forEach((layer) => {
      for (let i = 0; i < layer.count; i++) {
        const size = layer.sizeRange[0] + Math.random() * (layer.sizeRange[1] - layer.sizeRange[0]);
        const opacity = layer.opacity[0] + Math.random() * (layer.opacity[1] - layer.opacity[0]);
        fishes.push({
          x: Math.random() * W,
          y: H * (layer.yRange[0] + Math.random() * (layer.yRange[1] - layer.yRange[0])),
          size,
          opacity,
          speed: layer.speed * (0.7 + Math.random() * 0.6),
          direction: layer.direction,
          wobblePhase: Math.random() * Math.PI * 2,
          wobbleSpeed: 0.5 + Math.random() * 1.5,
          wobbleAmp: 2 + Math.random() * 4,
          depth: layer.depth,
          // 每条鱼有微小的个体差异
          brightness: 0.6 + Math.random() * 0.4,
        });
      }
    });
    fishDataRef.current = fishes;

    let lastTime = performance.now();

    const animate = (now) => {
      const dt = (now - lastTime) / 1000;
      lastTime = now;
      const containerRect = container.getBoundingClientRect();
      const cW = containerRect.width;
      const cH = containerRect.height;
      const mouse = mouseRef.current;

      // transform 操作尽可能批量
      const transforms = fishDataRef.current.map((fish) => {
        // 基础移动
        fish.x += fish.direction * fish.speed * dt;

        // 超出边界时从另一边出现
        if (fish.direction > 0 && fish.x > cW + fish.size) {
          fish.x = -fish.size * 2;
          fish.y = cH * (SCHOOL_LAYERS[fish.depth - 1].yRange[0] + Math.random() * 0.2);
        } else if (fish.direction < 0 && fish.x < -fish.size * 2) {
          fish.x = cW + fish.size;
          fish.y = cH * (SCHOOL_LAYERS[fish.depth - 1].yRange[0] + Math.random() * 0.2);
        }

        // 上下浮动
        const wobble = Math.sin(now / 1000 * fish.wobbleSpeed + fish.wobblePhase) * fish.wobbleAmp;

        // 鼠标避让（非常缓慢）
        let avoidX = 0;
        const dx = fish.x - mouse.x;
        const dy = fish.y - mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 80 && dist > 0) {
          const force = (80 - dist) / 80 * 15;
          avoidX = (dx / dist) * force;
        }

        return `translate(${fish.x + avoidX}px, ${fish.y + wobble}px) scaleX(${fish.direction})`;
      });

      // 批量更新 DOM
      const nodes = container.children;
      for (let i = 0; i < nodes.length; i++) {
        nodes[i].style.transform = transforms[i];
      }

      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);

    const handleMouseMove = (e) => {
      const r = container.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    // 只在非触摸设备启用鼠标交互
    if (!('ontouchstart' in window)) {
      container.parentElement?.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      container.parentElement?.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 3,
        overflow: 'hidden',
      }}
    >
      {fishDataRef.current.length === 0 &&
        SCHOOL_LAYERS.flatMap((layer, li) =>
          Array.from({ length: layer.count }).map((_, i) => {
            const size = layer.sizeRange[0] + Math.random() * (layer.sizeRange[1] - layer.sizeRange[0]);
            const opacity = layer.opacity[0] + Math.random() * (layer.opacity[1] - layer.opacity[0]);
            const yPct = layer.yRange[0] + Math.random() * (layer.yRange[1] - layer.yRange[0]);
            return (
              <div
                key={`fish-${li}-${i}`}
                style={{
                  position: 'absolute',
                  top: `${yPct * 100}%`,
                  left: 0,
                  width: `${size}px`,
                  height: `${size * 0.5}px`,
                  opacity,
                  filter: `brightness(${0.6 + Math.random() * 0.4})`,
                  transition: 'transform 0.3s ease-out',
                  willChange: 'transform',
                }}
              >
                <img
                  src="/assets/ocean/fish/fish-blue.svg"
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </div>
            );
          })
        )}
    </div>
  );
}
